import { memo, useCallback, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Button } from '@heroui/react';
import { CHAT_MESSAGE_TEXT_MAX } from '../../config/constants';
import { IconClose, IconPaperclip, IconSend } from '../icons';
import type { ChatMessage } from '../../model/types';

const TEXTAREA_MAX_PX = 140;
const TEXTAREA_MIN_PX = 24;

export type ChatComposerProps = {
  value: string;
  attachments: File[];
  replyToMessage: ChatMessage | null;
  focusRequestId?: number;
  onChange: (v: string) => void;
  onSend: () => void;
  onAttachFiles: (files: File[]) => void;
  onRemoveAttachment: (index: number) => void;
  onClearReply: () => void;
  placeholder?: string;
  disabled?: boolean;
};

const iconBtnClass =
  'min-w-10 min-h-10 text-[#1E1E1E] hover:bg-black/[0.04] data-[hover=true]:bg-black/[0.04]';

export const ChatComposer = memo(
  ({
    value,
    attachments,
    replyToMessage,
    focusRequestId = 0,
    onChange,
    onSend,
    onAttachFiles,
    onRemoveAttachment,
    onClearReply,
    placeholder = 'Write a message...',
    disabled = false,
  }: ChatComposerProps) => {
    const taRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const attachButtonRef = useRef<HTMLButtonElement>(null);
    const focusTimeoutsRef = useRef<number[]>([]);
    const [draftPreviewUrls, setDraftPreviewUrls] = useState<(string | null)[]>([]);
    const textLength = value.trim().length;
    const isOverLimit = textLength > CHAT_MESSAGE_TEXT_MAX;
    const canSend = (textLength > 0 || attachments.length > 0) && !isOverLimit;

    useEffect(() => {
      const urls = attachments.map((file) => {
        if (file.type.startsWith('image/') || file.type.startsWith('video/')) {
          return URL.createObjectURL(file);
        }
        return null;
      });
      setDraftPreviewUrls(urls);
      return () => {
        urls.forEach((url) => {
          if (url) URL.revokeObjectURL(url);
        });
      };
    }, [attachments]);

    const focusTextarea = useCallback(() => {
      focusTimeoutsRef.current.forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });
      focusTimeoutsRef.current = [];

      const focusNow = () => {
        const activeElement = document.activeElement;
        if (activeElement instanceof HTMLElement && activeElement !== taRef.current) {
          activeElement.blur();
        }

        taRef.current?.focus({ preventScroll: true });
      };

      window.requestAnimationFrame(focusNow);
      focusTimeoutsRef.current.push(window.setTimeout(focusNow, 80));
      focusTimeoutsRef.current.push(window.setTimeout(focusNow, 180));
    }, []);

    const syncHeight = useCallback(() => {
      const el = taRef.current;
      if (!el) return;
      el.style.height = '0px';
      const next = Math.min(Math.max(el.scrollHeight, TEXTAREA_MIN_PX), TEXTAREA_MAX_PX);
      el.style.height = `${next}px`;
    }, []);

    useLayoutEffect(() => {
      syncHeight();
    }, [value, syncHeight]);

    useEffect(() => () => {
        focusTimeoutsRef.current.forEach((timeoutId) => {
          window.clearTimeout(timeoutId);
        });
      }, []);

    useEffect(() => {
      if (replyToMessage) {
        focusTextarea();
      }
    }, [focusTextarea, replyToMessage]);

    useEffect(() => {
      if (focusRequestId > 0) {
        focusTextarea();
      }
    }, [focusRequestId, focusTextarea]);

    const onKeyDown = useCallback(
      (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault();
          if (canSend && !disabled) onSend();
        }
      },
      [canSend, disabled, onSend],
    );

    return (
      <div className="shrink-0 bg-[#F5F5F5] px-4 py-3">
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            const files = Array.from(e.target.files ?? []);
            if (files.length) {
              onAttachFiles(files);
              focusTextarea();
            }
            e.currentTarget.value = '';
            attachButtonRef.current?.blur();
          }}
        />
        <div
          data-focus-ring="wrapper"
          className={`w-full rounded-xl border bg-[#FFFFFF] ${isOverLimit ? 'border-danger' : 'border-[#1E1E1E]'}`}
        >
          {replyToMessage ? (
            <div className="flex items-start justify-between gap-3 border-b border-black/10 px-4 py-3">
              <div className="min-w-0 flex-1 border-l-2 border-[#1E1E1E] pl-3">
                <p className="text-xs font-semibold text-[#1E1E1E]">{replyToMessage.authorName}</p>
                <p className="mt-1 line-clamp-2 whitespace-pre-wrap break-words text-sm leading-5 text-[#5F5F5F]">
                  {replyToMessage.text?.trim() ||
                    (replyToMessage.attachments.length
                      ? `Attachment: ${replyToMessage.attachments[0].name}`
                      : 'Message')}
                </p>
              </div>
              <button
                type="button"
                className="shrink-0 text-black/55 transition-opacity hover:opacity-80"
                onClick={() => {
                  onClearReply();
                  focusTextarea();
                }}
                aria-label="Cancel reply"
              >
                <IconClose className="h-4 w-4" />
              </button>
            </div>
          ) : null}
          {attachments.length > 0 ? (
            <div className="flex flex-wrap gap-2 border-b border-black/10 px-4 py-3">
              {attachments.map((attachment, index) => {
                const previewUrl = draftPreviewUrls[index];
                const isVideo = attachment.type.startsWith('video/');
                const isImage = attachment.type.startsWith('image/');

                return (
                  <div
                    key={`${attachment.name}-${attachment.size}-${index}`}
                    className="flex max-w-full items-center gap-2 rounded-full bg-black/[0.04] px-3 py-1.5 text-sm text-[#1E1E1E]"
                >
                    {previewUrl && (isImage || isVideo) ? (
                    isVideo ? (
                      <video
                        src={previewUrl}
                        muted
                        playsInline
                        preload="metadata"
                        className="h-10 w-10 shrink-0 rounded-lg object-cover"
                      />
                    ) : (
                      <img
                        src={previewUrl}
                        alt=""
                        className="h-10 w-10 shrink-0 rounded-lg object-cover"
                      />
                    )
                  ) : (
                    <IconPaperclip className="h-4 w-4 shrink-0" />
                  )}
                    <span className="max-w-[220px] truncate">{attachment.name}</span>
                    <button
                      type="button"
                      className="shrink-0 text-black/55 transition-opacity hover:opacity-80"
                      onClick={() => onRemoveAttachment(index)}
                      aria-label={`Remove ${attachment.name}`}
                  >
                      <IconClose className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : null}
          <div className="flex min-h-[40px] w-full items-end gap-3 px-4">
            <textarea
              ref={taRef}
              className="min-w-0 flex-1 resize-none overflow-y-auto bg-transparent py-2.5 text-lg leading-5 text-[#1E1E1E] outline-none placeholder:text-default-400 disabled:cursor-not-allowed disabled:opacity-60"
              rows={1}
              placeholder={placeholder}
              value={value}
              disabled={disabled}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={onKeyDown}
              aria-label={placeholder}
              aria-invalid={isOverLimit}
              aria-describedby="chat-composer-meta"
          />
            {/* После диалога выбора файлов браузер возвращает фокус на кнопку, React Aria считает
                его клавиатурным — и вокруг скрепки оставалась обводка */}
            <Button
              ref={attachButtonRef}
              isIconOnly
              variant="light"
              radius="full"
              className={`${iconBtnClass} !outline-none data-[focus-visible=true]:!outline-none`}
              aria-label="Attach file"
              isDisabled={disabled}
              onPress={() => {
                fileInputRef.current?.click();
                attachButtonRef.current?.blur();
              }}
          >
              <IconPaperclip />
            </Button>
            <Button
              isIconOnly
              variant="light"
              radius="full"
              className={iconBtnClass}
              aria-label="Send message"
              isDisabled={disabled || !canSend}
              onPress={onSend}
          >
              <IconSend />
            </Button>
          </div>
        </div>
        <div id="chat-composer-meta" className="mt-1.5 flex items-start justify-between gap-3 px-1">
          <p className="min-w-0 text-xs leading-4 text-danger" role={isOverLimit ? 'alert' : undefined}>
            {isOverLimit ? `Text must be at most ${CHAT_MESSAGE_TEXT_MAX} characters` : '\u00a0'}
          </p>
          <p
            className={`shrink-0 text-xs tabular-nums leading-4 ${isOverLimit ? 'font-medium text-danger' : 'text-[#5F5F5F]'}`}
          >
            {textLength}/{CHAT_MESSAGE_TEXT_MAX}
          </p>
        </div>
      </div>
    );
  },
);

ChatComposer.displayName = 'ChatComposer';
